import { defineDirective, fields, getInitialState, ITimelineContext, LocaleString, TimelineState } from "../../omnia"
import { IFieldsEditService, IFieldsModuleContext } from "../../types"
import { getCommands } from "../contextMenu"
import { Timeline } from "./Timeline"
import { TimelineProps } from "./types"

import tl = omnia.fields.timeline
import { updateEditMode, updateEditModeSelection } from "../selection"

export class ReactCtrl {

    static create() {

        const def = defineDirective(ReactCtrl, "app/fields/timeline/react/view.html", {
            context: "=",
            componentWidth: "=",
            componentsWidth: "=",
            isActive: "="
        })

        def.link = (scope, elt, attrs, ctrl: ReactCtrl) => ctrl.link(elt[0])

        return def

    }

    private reactRoot: HTMLElement

    private refreshInProgress = false

    public isActive = false
    public debug = false
    public showIds = false

    context: IFieldsModuleContext
    componentWidth: number
    componentsWidth: number

        private years: {
            current: number,
            firstYears: number[],
            lastYears: number[],
            range: tl.Years,
        }

    public zoomOptions = [
        { value: 150, text: 'Large' as LocaleString },
        { value: 125, text: 'Medium' as LocaleString },
        { value: 100, text: 'Small' as LocaleString }
    ]

    private state: TimelineState
    private ctrl: tl.ITimelineCtrl

    static $inject = ["$scope", "$timeout", "OmniaFieldsEditService"]

    constructor(private $scope: ng.IScope, private $timeout: ng.ITimeoutService, public readonly editSvc: IFieldsEditService) {
    }

    $onInit() {

        const { shell } = this.context
        const session = shell.getSession()

        this.debug = session.debugMode
        this.showIds = shell.showIds

        if (this.debug) {
            this.$scope.$watch(() => shell.showIds, (a, b) => {
                if (a !== b) {
                    this.showIds = a
                    this.refresh()
                }
            })
        }

        this.setup()

    }

    $onDestroy() {
        if (this.reactRoot) {
            ReactDOM.unmountComponentAtNode(this.reactRoot)
            this.reactRoot = undefined
        }
    }

    private link(elt: Element) {
        this.reactRoot = elt.querySelector(".react-host")
        this.setup()
    }

    private setup() {

        if (!(this.$scope && this.reactRoot)) {
            return
        }

        const { svc } = this.context
        fields.watchFields(svc, this.$scope, () => this.refresh())
        this.$scope.$watch("vm.isActive", () => this.refresh())

        this.$scope.$watchGroup(["vm.editSvc.targetType", "vm.editSvc.multiple"], () => updateEditMode(this.ctrl))
        this.$scope.$watchCollection("vm.editSvc.cropping", () => updateEditModeSelection(this.ctrl, false))


    }

    refresh() {

        if (!this.isActive) return
        if (this.refreshInProgress) return

        const { svc } = this.context

        this.years = (this.years?.current == svc.yearId)
            ? this.years
            : tl.setupYears(this.context.svc)

        this.refreshInProgress = true

        this.$timeout().then(() => {

            try {

                const fields = svc.getFields(true).filter(f => f.visible && ((f.croppings.length == 0) || f.croppings.some(c => c.visible)))

                const viewConfig = tl.getDefaultViewConfig({
                    durationScale: tl.viewState.timelineZoom / 100,
                })

                const state = this.state = getInitialState(
                    this.editSvc,
                    fields,
                    svc.cropYear,
                    this.years.range,
                    viewConfig,
                    this.showIds
                )

                const ctrl = this.ctrl = state.controller

                ctrl.render = () => this.render()
                ctrl.isEditing = () => this.isEditing()
                ctrl.setZoomLevelWheel = s => this.setZoomLevelWheel(s)

                const start = Date.now()

                this.render()

                if (this.debug) {

                    window.setTimeout(() => {
                        const d = Date.now() - start
                        console.log("timeline.refresh", d)
                        this.$scope.$applyAsync()
                    }, 0)

                }

            }
            finally {

                this.refreshInProgress = false

            }

        })

    }


    render() {

        const { shell, svc } = this.context

        const context: ITimelineContext = {
            shell,
            svc,
            getPopupCommands: target => getCommands(target, this.context)
        }

        const render = () => Timeline(<TimelineProps>{
            context,
            initState: this.state,
            apply: () => this.$scope.$applyAsync()
        })

        const element = React.createElement(render)

        ReactDOM.render(element, this.reactRoot)

    }

    get ready() {
        return !this.refreshInProgress
    }

    get firstYear() {
        return this.years ? this.years.range.first : undefined
    }

    set firstYear(value) {
        this.years.range = tl.changeYearRange(this.years.range, value, true)
        this.refresh()
    }

    get firstYears() {
        return this.years ? this.years.firstYears : undefined
    }

    get lastYears() {
        return this.years ? this.years.lastYears : undefined
    }

    get lastYear() {
        return this.years ? this.years.range.last : undefined
    }

    set lastYear(value) {
        this.years.range = tl.changeYearRange(this.years.range, value, false)
        this.refresh()
    }

    get timelineZoom() {
        return tl.viewState.timelineZoom
    }

    set timelineZoom(value) {
        tl.viewState.timelineZoom = value
        this.refresh()
    }

    public setZoomLevelWheel(scale: number) {

        const vc = this.ctrl ? this.ctrl.viewConfig : null

        if (vc) {
            scale = vc.durationScale + scale
            scale = Math.max(vc.durationScaleMin, Math.min(scale, vc.durationScaleMax))
            vc.durationScale = scale
            this.timelineZoom = scale * 100
            this.$scope.$applyAsync()
        }

    }

    isEditing() {
        return !!this.editSvc.targetType
    }

    get items() {
        return this.state ? this.state.vm.allitems : null
    }

    get popupContext() {
        return this.ctrl ? this.ctrl.popupContext : null
    }

    set popupContext(value) {
        this.ctrl.popupContext = value
    }

}
